import { useEffect, useMemo, useState, useCallback } from "react";
import api, { inr } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { X, Filter, FileText, ChevronRight, Search } from "lucide-react";

const fmtPeriod = (s) => String(s || "").replace(/_/g, " ").replace(/\bto\b/, "→");
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtTime = (s) => (s ? new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) + " IST" : "—");
const fmtDateTime = (s) => (s ? `${fmtDate(s)} · ${fmtTime(s)}` : "—");

function StatusPill({ status, gross_rs }) {
  // Same semantics as the distributor view: closing status is implicit (every
  // row is a committed closing). This pill reports PAYMENT status explicitly:
  // Paid vs Zero-Payout vs Payment-Pending — never generic "Pending".
  const zero = Number(gross_rs || 0) === 0;
  if (status === "paid" && !zero) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800" data-testid="admin-wph-status">
        ✓ Paid
      </span>
    );
  }
  if (status === "paid" && zero) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600" data-testid="admin-wph-status">
        — Zero Payout
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800" data-testid="admin-wph-status">
      ⏱ Payment Pending
    </span>
  );
}

function AdminDetailModal({ row, onClose }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    if (!row) return;
    setBusy(true);
    api.get(`/admin/weekly-payouts/${row.id}`)
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load detail"))
      .finally(() => setBusy(false));
  }, [row]);
  if (!row) return null;
  const d = detail || row;
  const tier = d.monthly_bonus_tier || {};
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" onClick={onClose} data-testid="admin-wph-detail-modal">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-white rounded-lg border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-gradient-to-r from-navy to-navy/90 text-white px-5 py-3.5 flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gold">Weekly Closing Detail (Admin)</div>
            <div className="font-serif text-lg">{d.user_code} · {d.name}</div>
            <div className="text-[11px] text-white/60">{fmtPeriod(d.period_label)} · Closed {fmtDate(d.closed_at)}</div>
          </div>
          <button data-testid="admin-wph-close" onClick={onClose} className="text-white/70 hover:text-white p-1" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-navy/5 border border-navy/10 rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-navy/50">Gross</div>
              <div className="text-2xl text-navy tabular-nums">{inr(d.gross_rs)}</div>
            </div>
            <div className="bg-rose/5 border border-rose/10 rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-rose/70">TDS ({Number(d.tds_rate_pct || 0).toFixed(0)}%)</div>
              <div className="text-2xl text-rose tabular-nums">− {inr(d.tds_amount_rs)}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
              <div className="text-[9px] uppercase tracking-widest text-emerald-700">Net</div>
              <div className="text-2xl text-emerald-800 tabular-nums">{inr(d.net_rs)}</div>
            </div>
            <div className="bg-white border border-border rounded-sm p-3 flex flex-col justify-between">
              <div className="text-[9px] uppercase tracking-widest text-navy/50">Payment Status</div>
              <StatusPill status={d.status} gross_rs={d.gross_rs} />
              {d.paid_at ? <div className="text-[10px] text-navy/50 mt-1">Paid on {fmtDate(d.paid_at)}</div> : null}
            </div>
          </div>

          <section>
            <h4 className="text-[10px] uppercase tracking-widest text-navy/50 font-bold mb-2">Commission Breakdown</h4>
            <table className="w-full text-sm">
              <tbody>
                {d.pair_income_rs > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-2 text-navy/80">Pair Matching (1:1) — {d.matched_sp} SP · {d.matched_pairs} pairs</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy font-semibold">{inr(d.pair_income_rs)}</td>
                  </tr>
                )}
                {d.direct_ref_rs > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-2 text-navy/80">Direct Referral 10%</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy font-semibold">{inr(d.direct_ref_rs)}</td>
                  </tr>
                )}
                {d.monthly_bonus_rs > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-2 text-navy/80">
                      Monthly Bonus{tier.tier ? ` — Tier ${tier.tier} (${tier.high_sp}:${tier.match_sp} SP)` : ""}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy font-semibold">{inr(d.monthly_bonus_rs)}</td>
                  </tr>
                )}
                {d.rank_reward_rs > 0 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-2 text-navy/80">Rank Reward</td>
                    <td className="px-3 py-2 text-right tabular-nums text-navy font-semibold">{inr(d.rank_reward_rs)}</td>
                  </tr>
                )}
                <tr className="bg-navy">
                  <td className="px-3 py-2 text-gold font-extrabold uppercase tracking-wide text-xs">Gross</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gold font-extrabold">{inr(d.gross_rs)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h4 className="text-[10px] uppercase tracking-widest text-navy/50 font-bold mb-2">Leg SP · This Closing</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy/50">Left SP</div>
                <div className="text-sm tabular-nums text-navy mt-1">
                  <span className="text-navy/60">Before</span> {d.left_sp_before} <span className="text-navy/40 mx-1">→</span>
                  <span className="font-bold text-emerald-700">After</span> <span className="text-emerald-700 font-extrabold">{d.left_sp_after}</span>
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-navy/50">Right SP</div>
                <div className="text-sm tabular-nums text-navy mt-1">
                  <span className="text-navy/60">Before</span> {d.right_sp_before} <span className="text-navy/40 mx-1">→</span>
                  <span className="font-bold text-emerald-700">After</span> <span className="text-emerald-700 font-extrabold">{d.right_sp_after}</span>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4 className="text-[10px] uppercase tracking-widest text-navy/50 font-bold mb-2">Carry-forward into Next Closing</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50/70 border border-emerald-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700">Pair Carry L / R</div>
                <div className="text-xl text-emerald-800 tabular-nums">
                  {d.pair_carry_left_sp} <span className="text-emerald-800/40 mx-1">/</span> {d.pair_carry_right_sp}
                  <span className="text-emerald-700/60 ml-2 text-xs">SP</span>
                </div>
              </div>
              <div className="bg-emerald-50/70 border border-emerald-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700">Bonus Carry L / R</div>
                <div className="text-xl text-emerald-800 tabular-nums">
                  {d.bonus_carry_left_sp} <span className="text-emerald-800/40 mx-1">/</span> {d.bonus_carry_right_sp}
                  <span className="text-emerald-700/60 ml-2 text-xs">SP</span>
                </div>
              </div>
            </div>
          </section>

          {busy ? (
            <div className="text-xs text-navy/50 italic">Loading ledger entries…</div>
          ) : (d.incomes || []).length > 0 ? (
            <section>
              <h4 className="text-[10px] uppercase tracking-widest text-navy/50 font-bold mb-2">
                Underlying Income Entries ({(d.incomes || []).length})
              </h4>
              <div className="border border-border rounded-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-navy/5">
                    <tr>
                      {["Date", "Type", "Matched SP", "Amount", "Paid Out"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 uppercase tracking-wider font-medium text-navy/60">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(d.incomes || []).map((i) => (
                      <tr key={i.id} className="border-t border-border">
                        <td className="px-3 py-1.5 text-navy/70 tabular-nums">{fmtDate(i.created_at)}</td>
                        <td className="px-3 py-1.5 text-navy capitalize">{String(i.type || "").replace(/_/g, " ")}</td>
                        <td className="px-3 py-1.5 text-navy/70 tabular-nums">{i.matched_sp ?? "—"}</td>
                        <td className="px-3 py-1.5 text-navy font-semibold tabular-nums">{inr(i.amount)}</td>
                        <td className="px-3 py-1.5">
                          {i.paid_out ? <span className="text-emerald-700 text-[10px] font-bold">✓ paid</span> : <span className="text-amber-700 text-[10px] font-bold">— pending</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <div className="text-[10px] text-navy/50 italic border-t border-border pt-3">
            This record is append-only. Once committed, weekly closings are permanent and cannot be altered.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminWeeklyPayoutHistory() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_gross_rs: 0, total_tds_rs: 0, total_net_rs: 0 });
  const [periodOptions, setPeriodOptions] = useState([]);
  const [distinctPeriods, setDistinctPeriods] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [period, setPeriod] = useState("");
  const [userCode, setUserCode] = useState("");
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (fromDate) qs.set("from_date", fromDate);
    if (toDate) qs.set("to_date", toDate);
    if (period) qs.set("period_label", period);
    if (userCode) qs.set("user_code", userCode.trim().toUpperCase());
    api.get(`/admin/weekly-payouts${qs.toString() ? `?${qs.toString()}` : ""}`)
      .then((r) => {
        setRows(r.data.rows || []);
        setTotals({
          total_gross_rs: r.data.total_gross_rs || 0,
          total_tds_rs:   r.data.total_tds_rs   || 0,
          total_net_rs:   r.data.total_net_rs   || 0,
        });
        setPeriodOptions(r.data.period_labels || []);
        setDistinctPeriods(r.data.distinct_period_count || 0);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load history"))
      .finally(() => setLoading(false));
  }, [fromDate, toDate, period, userCode]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setFromDate(""); setToDate(""); setPeriod(""); setUserCode(""); };

  return (
    <DashboardLayout
      title="Weekly Payout History"
      subtitle={`${distinctPeriods} weekly closing${distinctPeriods === 1 ? "" : "s"} · ${rows.length} IBA payout${rows.length === 1 ? "" : "s"} · read-only · permanent`}
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5">
        <div className="bg-white border border-border rounded-sm p-4" data-testid="admin-wph-count-closings">
          <div className="text-[10px] uppercase tracking-widest text-navy/50">Weekly Closings</div>
          <div className="text-2xl text-navy tabular-nums">{distinctPeriods}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-4" data-testid="admin-wph-count">
          <div className="text-[10px] uppercase tracking-widest text-navy/50">IBA Payouts</div>
          <div className="text-2xl text-navy tabular-nums">{rows.length}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-navy/50">Gross</div>
          <div className="text-2xl text-navy tabular-nums">{inr(totals.total_gross_rs)}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-rose/70">TDS</div>
          <div className="text-2xl text-rose tabular-nums">− {inr(totals.total_tds_rs)}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Net</div>
          <div className="text-2xl text-emerald-800 tabular-nums">{inr(totals.total_net_rs)}</div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-navy/50" />
          <span className="text-[10px] uppercase tracking-widest text-navy/50 font-bold">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">From date</span>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              data-testid="admin-wph-filter-from"
              className="w-full mt-1 border border-border rounded-sm px-3 py-2 text-sm focus:border-gold outline-none" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">To date</span>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              data-testid="admin-wph-filter-to"
              className="w-full mt-1 border border-border rounded-sm px-3 py-2 text-sm focus:border-gold outline-none" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">Closing period</span>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}
              data-testid="admin-wph-filter-period"
              className="w-full mt-1 border border-border rounded-sm px-3 py-2 text-sm focus:border-gold outline-none bg-white">
              <option value="">All periods</option>
              {periodOptions.map((p) => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-navy/50">IBA code</span>
            <div className="relative mt-1">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-navy/40" />
              <input type="text" placeholder="SM747533" value={userCode}
                onChange={(e) => setUserCode(e.target.value)}
                data-testid="admin-wph-filter-user"
                className="w-full border border-border rounded-sm pl-7 pr-3 py-2 text-sm font-mono uppercase focus:border-gold outline-none" />
            </div>
          </label>
          <div className="flex items-end gap-2">
            <button onClick={clearFilters}
              data-testid="admin-wph-filter-clear"
              className="w-full px-3 py-2 text-xs uppercase tracking-widest text-navy/60 border border-border hover:bg-slate-50 rounded-sm">
              <X size={12} className="inline mr-1" /> Clear
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1300px]">
          <thead className="bg-navy text-white">
            <tr>
              {[
                "#", "Closed On", "Paid On", "IBA", "Name", "Left → After", "Right → After", "Matched Pairs",
                "Pair ₹", "Dref ₹", "Bonus ₹", "Gross ₹", "TDS", "Net ₹",
                "Pair Carry", "Bonus Carry", "Payment Status", ""
              ].map((h) => (
                <th key={h} className="text-left px-3 py-3 text-[10px] uppercase tracking-widest font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={18} className="p-6 text-center text-navy/50">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={18} className="p-6 text-center text-navy/50" data-testid="admin-wph-empty">No matching weekly payouts</td></tr>
            )}
            {!loading && (() => {
              const groups = [];
              rows.forEach((r) => {
                const g = groups[groups.length - 1];
                if (g && g.period === r.period_label) g.rows.push(r);
                else groups.push({ period: r.period_label, rows: [r] });
              });
              const bands = ["bg-white", "bg-slate-50/40"];
              return groups.flatMap((g, gi) => {
                const grossSum = g.rows.reduce((s, r) => s + (r.gross_rs || 0), 0);
                const netSum   = g.rows.reduce((s, r) => s + (r.net_rs   || 0), 0);
                const closed_on = g.rows[0]?.closed_at;
                const paid_on   = g.rows[0]?.paid_at;
                return [
                  <tr key={`h-${g.period}`} data-testid={`admin-wph-group-${g.period}`} className="bg-navy/5">
                    <td colSpan={18} className="px-3 py-2 text-navy border-t border-b-2 border-navy/20">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                        <span className="text-[10px] uppercase tracking-widest text-navy/50 font-bold">Weekly Closing</span>
                        <span className="font-serif text-base text-navy">{fmtPeriod(g.period)}</span>
                        <span className="text-xs text-navy/60">· Closed {fmtDateTime(closed_on)}</span>
                        {paid_on ? <span className="text-xs text-emerald-700">· Paid {fmtDateTime(paid_on)}</span> : null}
                        <span className="ml-auto text-xs text-navy/70">
                          <span className="tabular-nums font-bold text-navy">{g.rows.length}</span> IBA payout{g.rows.length === 1 ? "" : "s"}
                          <span className="mx-2 text-navy/30">·</span>
                          Gross <span className="tabular-nums font-bold text-navy">{inr(grossSum)}</span>
                          <span className="mx-2 text-navy/30">·</span>
                          Net <span className="tabular-nums font-bold text-emerald-700">{inr(netSum)}</span>
                        </span>
                      </div>
                    </td>
                  </tr>,
                  ...g.rows.map((r, ri) => (
                    <tr
                      key={r.id}
                      data-testid={`admin-wph-row-${r.id}`}
                      onClick={() => setOpenRow(r)}
                      className={`${bands[gi % bands.length]} border-b border-border hover:bg-cream/40 cursor-pointer`}
                    >
                      <td className="px-3 py-2.5 text-navy/50 tabular-nums text-xs">{ri + 1}<span className="text-navy/30">/{g.rows.length}</span></td>
                      <td className="px-3 py-2.5 text-navy/70 text-xs whitespace-nowrap">
                        {fmtDate(r.closed_at)}
                        <div className="text-navy/40 text-[10px] tabular-nums">{fmtTime(r.closed_at)}</div>
                      </td>
                      <td className="px-3 py-2.5 text-navy/70 text-xs whitespace-nowrap">
                        {r.paid_at ? fmtDate(r.paid_at) : "—"}
                        {r.paid_at ? <div className="text-emerald-700/70 text-[10px] tabular-nums">{fmtTime(r.paid_at)}</div> : null}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-navy">{r.user_code}</td>
                      <td className="px-3 py-2.5 text-navy/80 whitespace-nowrap">{(r.name || "").slice(0, 22)}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/70">{r.left_sp_before} → <span className="text-navy font-semibold">{r.left_sp_after}</span></td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/70">{r.right_sp_before} → <span className="text-navy font-semibold">{r.right_sp_after}</span></td>
                      <td className="px-3 py-2.5 tabular-nums text-navy">{r.matched_pairs}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">{r.pair_income_rs > 0 ? inr(r.pair_income_rs) : "—"}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">{r.direct_ref_rs > 0 ? inr(r.direct_ref_rs) : "—"}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">{r.monthly_bonus_rs > 0 ? inr(r.monthly_bonus_rs) : "—"}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy font-semibold">{inr(r.gross_rs)}</td>
                      <td className="px-3 py-2.5 tabular-nums text-rose">− {inr(r.tds_amount_rs)}</td>
                      <td className="px-3 py-2.5 tabular-nums text-emerald-700 font-bold">{inr(r.net_rs)}</td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">
                        <span className="text-emerald-700">{r.pair_carry_left_sp}</span><span className="text-navy/40 mx-1">/</span><span className="text-emerald-700">{r.pair_carry_right_sp}</span>
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-navy/80">
                        <span className="text-emerald-700">{r.bonus_carry_left_sp}</span><span className="text-navy/40 mx-1">/</span><span className="text-emerald-700">{r.bonus_carry_right_sp}</span>
                      </td>
                      <td className="px-3 py-2.5"><StatusPill status={r.status} gross_rs={r.gross_rs} /></td>
                      <td className="px-3 py-2.5 text-navy/40"><ChevronRight size={14} /></td>
                    </tr>
                  )),
                ];
              });
            })()}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[10px] text-navy/50 italic">
        <FileText size={10} className="inline mr-1" />
        Permanent audit ledger. Committed weekly closings can never be modified, deleted, or recalculated.
      </p>

      {openRow && <AdminDetailModal row={openRow} onClose={() => setOpenRow(null)} />}
    </DashboardLayout>
  );
}
